/**
 * Field-level evidence photos — which data-entry fields of which test types
 * carry an "Add Evidence" camera button, and which of those are REQUIRED
 * (missing required evidence drives the amber nag; save is never blocked).
 *
 * The stored key on ObservationMedia.FieldKey is `<testTypeId>.<fieldKey>`
 * (migration 115). testTypeId matches the Formula Registry ids
 * (useTestFormulas — 'rockUcs', 'rockTcs', …).
 *
 * Seeded in code for v1; the lab picks the final list once the feature is in
 * use (planned follow-up: admin-editable, to a point).
 */

export interface EvidenceFieldConfig {
  /** Short key unique within the test type (stored as `<testTypeId>.<fieldKey>`). */
  fieldKey: string;
  /** Human label shown in the evidence popover and nag. */
  label: string;
  /** Required fields count toward the "evidence missing" nag. */
  required: boolean;
}

/**
 * The lab's approved list (Vahid, 2026-07-24): PLT → Gauge PSI; Direct Shear →
 * JRC; BTS → Mass + Maximum Load at Failure (per specimen — BTS always has 2);
 * UCS, UCSM, TCS → Mass. Cerchar and the soil tests carry no field evidence.
 */
export const EVIDENCE_FIELDS: Record<string, EvidenceFieldConfig[]> = {
  // Rock Uniaxial Compressive Strength with Modulus (Rock UCSM)
  rockUcs: [
    { fieldKey: 'massG', label: 'Mass (as received)', required: true },
  ],
  // Rock Uniaxial Compressive Strength (No Strain) — shares the UCSM specimen section
  rockUcsNs: [
    { fieldKey: 'massG', label: 'Mass (as received)', required: true },
  ],
  // Rock Triaxial Compressive Strength
  rockTcs: [
    { fieldKey: 'massG', label: 'Mass (as received)', required: true },
  ],
  // Rock Brazilian Tensile Strength — always 2 specimens
  rockBts: [
    { fieldKey: 'mass1', label: 'Mass — Specimen 1', required: true },
    { fieldKey: 'mass2', label: 'Mass — Specimen 2', required: true },
    { fieldKey: 'maxLoad1', label: 'Maximum Load at Failure — Specimen 1', required: true },
    { fieldKey: 'maxLoad2', label: 'Maximum Load at Failure — Specimen 2', required: true },
  ],
  // Direct Shear
  directShear: [
    { fieldKey: 'jrc', label: 'JRC', required: true },
  ],
  // Point Load Test — per-sample rows in the batch grid (each row is its own test)
  pointLoad: [
    { fieldKey: 'gaugePsi', label: 'Gauge PSI', required: true },
  ],
};

/** Fully-qualified stored key. */
export const evidenceKey = (testTypeId: string, fieldKey: string): string =>
  `${testTypeId}.${fieldKey}`;

/**
 * Stable Test.TestCode (BUG-2026-0197) → evidence testTypeId, for listing pages
 * that dispatch on the code. The evidence testTypeId already equals the code for
 * every evidence-bearing type, so this is an identity map — kept explicit so the
 * evidence-bearing set is obvious and a renamed test still resolves.
 */
export const TEST_CODE_TO_EVIDENCE_TYPE: Record<string, string> = {
  rockUcs: 'rockUcs',
  rockUcsNs: 'rockUcsNs',
  rockTcs: 'rockTcs',
  rockBts: 'rockBts',
  directShear: 'directShear',
  pointLoad: 'pointLoad',
};

/** Required field count per test type (0 when the type has no evidence config). */
export const requiredEvidenceFields = (testTypeId: string | undefined): EvidenceFieldConfig[] =>
  (testTypeId ? EVIDENCE_FIELDS[testTypeId] ?? [] : []).filter((f) => f.required);
